import React, { useMemo } from 'react';
import { hasSecondaryPriceDisplay } from '@/domain/pricing/utils/pricing-utils';
import { ItemDetailContentData } from '@/experiences/sales-portal/domain/items/blocs/types/item-detail-content';
import { ItemDetailSection } from '@/experiences/sales-portal/domain/items/components/item-detail-section';
import {
  OrderEntryLabel,
  PriceOverrideInfo,
} from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { PriceEditType } from '@/types';
import { PriceOverrideInfoDisplay } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/order-entry-table/price-override-info';
import { PurchaseHistoryList } from '@/experiences/sales-portal/domain/items/components/purchase-history/purchase-history-list';
import { useGeneralTabSections } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/hooks/use-general-tab-sections';
import { YStack } from '@usepepper/sprout-kit-ui';

interface OrderEntryMobileGeneralTabProps {
  canSetManualOverrideExpiration: boolean;
  data: ItemDetailContentData;
  isGrossProfitHidden: boolean;
  isSalesRepInfoDisabled: boolean;
  labels: OrderEntryLabel | undefined;
  omitPriceAndGrossProfitRows: boolean;
  orderGuideLabels: { level: string; orderGuideName: string }[];
  priceOverrideInfo: PriceOverrideInfo | undefined;
  showPriceOverrideInfo: boolean;
  unitCount: number;
}

export const OrderEntryMobileGeneralTab: React.FC<
  OrderEntryMobileGeneralTabProps
> = ({
  canSetManualOverrideExpiration,
  data,
  isGrossProfitHidden,
  isSalesRepInfoDisabled,
  labels,
  omitPriceAndGrossProfitRows,
  orderGuideLabels,
  priceOverrideInfo,
  showPriceOverrideInfo,
  unitCount,
}) => {
  const override =
    showPriceOverrideInfo && priceOverrideInfo?.activeOverride !== undefined
      ? priceOverrideInfo
      : undefined;
  const showTemporaryIndicator =
    canSetManualOverrideExpiration && !!override?.isTemporaryOverride;
  const defaultPriceValue =
    override &&
    hasSecondaryPriceDisplay(unitCount) &&
    override.defaultUnitPriceDisplay
      ? `${override.defaultPackPriceDisplay} (${override.defaultUnitPriceDisplay})`
      : override?.defaultPackPriceDisplay;

  const grossProfitRowDetail = useMemo(
    () =>
      override ? (
        <PriceOverrideInfoDisplay
          defaultValue={override.defaultGrossProfitDisplay}
          editedByName={override.editedByName}
          expiryDateDisplay={override.expiryDateDisplay}
          isInline
          lastValueLabelKey="sales_portal.order_entry.table.temporary_adjustment.last_gross_profit"
          showOverrideIndicator={
            override.activeOverride === PriceEditType.GrossProfit
          }
          showTemporaryIndicator={showTemporaryIndicator}
        />
      ) : undefined,
    [override, showTemporaryIndicator],
  );
  const priceRowDetail = useMemo(
    () =>
      override ? (
        <PriceOverrideInfoDisplay
          defaultValue={defaultPriceValue ?? ''}
          editedByName={override.editedByName}
          expiryDateDisplay={override.expiryDateDisplay}
          isInline
          lastValueLabelKey="sales_portal.order_entry.table.temporary_adjustment.last_price"
          showOverrideIndicator={
            override.activeOverride === PriceEditType.Price
          }
          showTemporaryIndicator={showTemporaryIndicator}
        />
      ) : undefined,
    [defaultPriceValue, override, showTemporaryIndicator],
  );

  const sections = useGeneralTabSections({
    data,
    grossProfitRowDetail,
    isGrossProfitHidden,
    isSalesRepInfoDisabled,
    labels,
    omitPriceAndGrossProfitRows,
    orderGuideLabels,
    priceRowDetail,
  });

  return (
    <YStack gap="$16">
      {sections.map(({ key, ...section }) => (
        <ItemDetailSection key={key} {...section} />
      ))}
      <PurchaseHistoryList
        entries={data.purchaseHistory}
        hideGpField={isSalesRepInfoDisabled || isGrossProfitHidden}
      />
    </YStack>
  );
};
